// src/services/clubServices.js
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL;
const clubsUrl = `${API_BASE}/api/clubs`;
const usersUrl = `${API_BASE}/api/users`;

// Lit le token à chaque appel (utile si l’utilisateur se reconnecte)
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* =========================
   GET – Clubs
   ========================= */

// Tous les clubs
export const getClubs = async () => {
  try {
    const { data } = await axios.get(`${clubsUrl}`, { headers: authHeaders() });
    return data;
  } catch (error) {
    console.error("Erreur lors du chargement des clubs:", error);
    throw error;
  }
};

// Compter les clubs
export const countClubs = async () => {
  try {
    const { data } = await axios.get(`${clubsUrl}/count`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("Erreur lors du comptage des clubs:", error);
    throw error;
  }
};

// Détail d’un club
export const getClubById = async (clubId) => {
  try {
    const { data } = await axios.get(`${clubsUrl}/${clubId}`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("Erreur lors du chargement du club:", error);
    throw error;
  }
};

/* =========================
   GET – Relations User ↔ Club
   ========================= */

// Clubs d’un utilisateur (NOUVEAU)
export const getClubsByUser = async (userId) => {
  try {
    const { data } = await axios.get(`${usersUrl}/${userId}/clubs`, {
      headers: authHeaders(),
    });
    return data; // Array<Club>
  } catch (error) {
    console.error("Erreur lors du chargement des clubs de l'utilisateur:", error);
    throw error;
  }
};

// Membres d’un club (utilisateurs liés)
export const getMembersByClub = async (clubId) => {
  try {
    const { data } = await axios.get(`${clubsUrl}/${clubId}/members`, {
      headers: authHeaders(),
    });
    return data; // Array<UserLite & { role_in_club }>
  } catch (error) {
    console.error("Erreur lors du chargement des membres du club:", error);
    throw error;
  }
};

/* =========================
   POST/DELETE – Lier / Délier
   ========================= */

// Lier un utilisateur à un club
// payload: { user_id: number, role_in_club?: string }
export const linkUserToClub = async (clubId, payload) => {
  try {
    const { data } = await axios.post(`${clubsUrl}/${clubId}/members`, payload, {
      headers: authHeaders(),
    });
    return data; // { ok: true }
  } catch (error) {
    console.error("Erreur lors du lien user → club:", error);
    throw error;
  }
};

// Délier un utilisateur d’un club
export const unlinkUserFromClub = async (clubId, userId) => {
  try {
    const { data } = await axios.delete(`${clubsUrl}/${clubId}/members/${userId}`, {
      headers: authHeaders(),
    });
    return data; // { ok: true }
  } catch (error) {
    console.error("Erreur lors du délien user ← club:", error);
    throw error;
  }
};

/* =========================
   CRUD Club (optionnel / admin)
   ========================= */

export const createClub = async (clubData) => {
  try {
    const { data } = await axios.post(`${clubsUrl}`, clubData, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("Erreur lors de la création du club:", error);
    throw error;
  }
};

export const updateClub = async (clubId, clubData) => {
  try {
    const { data } = await axios.put(`${clubsUrl}/${clubId}`, clubData, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du club:", error);
    throw error;
  }
};

/* =========================
   PATCH – Mettre à jour le rôle d’un membre
   ========================= */
export const updateUserRoleInClub = async (clubId, userId, role_in_club) => {
  try {
    const { data } = await axios.patch(
      `${clubsUrl}/${clubId}/members/${userId}`,
      { role_in_club },
      { headers: authHeaders() }
    );
    return data; // { ok: true } (selon ton controller)
  } catch (error) {
    console.error("Erreur lors de la mise à jour du rôle dans le club:", error);
    throw error;
  }
};




export const deleteClub = async (clubId) => {
  try {
    const { data } = await axios.delete(`${clubsUrl}/${clubId}`, {
      headers: authHeaders(),
    });
    return data;
  } catch (error) {
    console.error("Erreur lors de la suppression du club:", error);
    throw error;
  }
};

/* =========================
   Compatibilité (legacy facultatif)
   ========================= */

// Ancienne route “club de l’utilisateur” (champ legacy Club.user_id)
// Préfère getClubsByUser(userId) quand c’est possible.
export const getClubByUser = async (userId) => {
  try {
    const { data } = await axios.get(`${clubsUrl}/user/${userId}`, {
      headers: authHeaders(),
    });
    return data; // Club ou null
  } catch (error) {
    console.error("Erreur (legacy) lors du chargement du club de l'utilisateur:", error);
    throw error;
  }
};

// Ancien helper “par enseignant” → alias vers getClubsByUser
export const getClubByTeacher = async (teacherId) => {
  try {
    const data = await getClubsByUser(teacherId);
    return data; // Array<Club>
  } catch (error) {
    console.error("Erreur lors de la récupération des clubs de l'enseignant:", error);
    throw error;
  }
};
